/**
 * MarketplaceCategoryNav
 * A persistent left-side navigation dock listing every marketplace category.
 * Drop into any marketplace page — it renders as a sticky left sidebar on
 * desktop and a scrollable horizontal pill strip on mobile.
 *
 * Design rationale:
 * - Fixed-position on desktop so it works with any existing page layout
 *   without requiring per-page grid/flex refactors.
 * - Collapsible via a toggle button so long product pages aren't crowded.
 * - Highlights the current category by matching against location.pathname
 *   or a `currentCategory` prop the page passes explicitly.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShoppingBag,
  Sparkles,
  Activity,
  Heart,
  Droplets,
  Waves,
  Leaf,
  Stethoscope,
  Store,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  FlaskConical,
  Scissors,
  Package,
  Ear,
  Microscope,
  Hand,
  Armchair,
  Grip,
  Droplet,
} from "lucide-react";

const CATEGORIES = [
  {
    key: "marketplace-home",
    name: "Marketplace Home",
    to: "/marketplace",
    icon: LayoutGrid,
    match: (p) => p === "/marketplace" || p === "/hospital/marketplace",
    accent: "text-slate-700",
    ring: "hover:bg-slate-100",
    activeBg: "bg-slate-900 text-white",
  },
  {
    key: "live-backorder-inventory",
    name: "Weekly Deals · Backorder List",
    to: "/backorders/live-inventory",
    icon: Package,
    match: (p) => p === "/backorders/live-inventory",
    accent: "text-amber-700",
    ring: "hover:bg-amber-50",
    activeBg: "bg-amber-500 text-slate-900",
    badge: "LIVE",
  },
  {
    key: "surgical-marketplace",
    name: "Surgical Marketplace",
    to: "/sterilemate/surgical",
    icon: Scissors,
    match: (p) => p === "/sterilemate/surgical",
    accent: "text-amber-700",
    ring: "hover:bg-amber-50",
    activeBg: "bg-amber-600 text-white",
  },
  {
    key: "american-surgical",
    name: "Neuro Patty Sponges",
    to: "/marketplace/american-surgical",
    icon: Stethoscope,
    match: (p) => p === "/marketplace/american-surgical",
    accent: "text-emerald-700",
    ring: "hover:bg-emerald-50",
    activeBg: "bg-emerald-600 text-white",
  },
  {
    key: "ent-bearent",
    name: "ENT — BEAR-ENT",
    to: "/sterilemate/ent-surgical",
    icon: Ear,
    match: (p) => p.startsWith("/sterilemate/ent-"),
    accent: "text-purple-700",
    ring: "hover:bg-purple-50",
    activeBg: "bg-purple-700 text-white",
  },
  {
    key: "solm-portfolio",
    name: "Sol-M Portfolio",
    to: "/sterilemate/diabetes-care",
    icon: Package,
    match: (p) =>
      p.startsWith("/sterilemate/") &&
      !p.startsWith("/sterilemate/ent-") &&
      p !== "/sterilemate/surgical",
    accent: "text-blue-700",
    ring: "hover:bg-blue-50",
    activeBg: "bg-blue-700 text-white",
  },
  {
    key: "gloves",
    name: "Disposable Gloves",
    to: "/marketplace/gloves",
    icon: Hand,
    match: (p) => p === "/marketplace/gloves" || p === "/hospital/marketplace/gloves",
    accent: "text-blue-700",
    ring: "hover:bg-blue-50",
    activeBg: "bg-blue-600 text-white",
  },
  {
    key: "healthcare-furniture",
    name: "Healthcare Examination Furniture",
    to: "/marketplace/healthcare-examination-furniture",
    icon: Armchair,
    match: (p) =>
      p === "/marketplace/furniture" ||
      p === "/marketplace/healthcare-examination-furniture" ||
      p.startsWith("/marketplace/furniture/"),
    accent: "text-slate-700",
    ring: "hover:bg-slate-100",
    activeBg: "bg-slate-800 text-white",
  },
  {
    key: "janitorial-evs",
    name: "Janitorial & EVS",
    to: "/marketplace/janitorial",
    icon: Sparkles,
    match: (p) => p.startsWith("/marketplace/janitorial") || p.startsWith("/hospital/marketplace/janitorial"),
    accent: "text-cyan-700",
    ring: "hover:bg-cyan-50",
    activeBg: "bg-cyan-600 text-white",
  },
  {
    key: "sterile-processing",
    name: "Sterile Processing",
    to: "/sterile-processing",
    icon: FlaskConical,
    match: (p, s) => p === "/sterile-processing" || s?.get?.("category") === "sterile-processing",
    accent: "text-indigo-700",
    ring: "hover:bg-indigo-50",
    activeBg: "bg-indigo-700 text-white",
  },
  {
    key: "hemodialysis",
    name: "Hemodialysis Product & Care",
    to: "/dialysis",
    icon: Droplets,
    match: (p) => p === "/dialysis",
    accent: "text-teal-700",
    ring: "hover:bg-teal-50",
    activeBg: "bg-teal-600 text-white",
  },
  {
    key: "water-purification",
    name: "Healthcare Water Purification",
    to: "/marketplace?category=water-purification",
    icon: Waves,
    match: (p, s) => s?.get?.("category") === "water-purification",
    accent: "text-sky-700",
    ring: "hover:bg-sky-50",
    activeBg: "bg-sky-600 text-white",
  },
  {
    key: "filmop",
    name: "Filmop USA",
    to: "/hospital/filmop-store",
    icon: ShoppingBag,
    match: (p) => p === "/hospital/filmop-store" || p === "/filmop-store",
    accent: "text-blue-700",
    ring: "hover:bg-blue-50",
    activeBg: "bg-blue-700 text-white",
  },
  {
    key: "medical-devices",
    name: "Medical Devices",
    to: "/marketplace/medical-devices",
    icon: Activity,
    match: (p) => p === "/marketplace/medical-devices" || p.startsWith("/marketplace/medical-devices/"),
    accent: "text-indigo-700",
    ring: "hover:bg-indigo-50",
    activeBg: "bg-indigo-600 text-white",
    subItems: [
      { key: "vive-power-mobility", name: "Power Mobility", to: "/marketplace/medical-devices/vive-power-mobility" },
      { key: "vive-mobility", name: "Mobility", to: "/marketplace/medical-devices/vive-mobility" },
      { key: "vive-bracing", name: "Bracing", to: "/marketplace/medical-devices/vive-bracing" },
      { key: "vive-health-devices", name: "Health Devices", to: "/marketplace/medical-devices/vive-health-devices" },
      { key: "vive-bathroom", name: "Bathroom", to: "/marketplace/medical-devices/vive-bathroom" },
      { key: "vive-bedroom", name: "Bedroom", to: "/marketplace/medical-devices/vive-bedroom" },
      { key: "vive-living-aids", name: "Living Aids", to: "/marketplace/medical-devices/vive-living-aids" },
      { key: "vive-footcare", name: "Footcare", to: "/marketplace/medical-devices/vive-footcare" },
      { key: "vive-physical-therapy", name: "Physical Therapy", to: "/marketplace/medical-devices/vive-physical-therapy" },
    ],
  },
  {
    key: "home-health-aids",
    name: "Home Health Aids",
    to: "/marketplace?category=home-health-aids",
    icon: Heart,
    match: (p, s) => s?.get?.("category") === "home-health-aids",
    accent: "text-rose-600",
    ring: "hover:bg-rose-50",
    activeBg: "bg-rose-600 text-white",
  },
  {
    key: "diabetes-management",
    name: "Diabetes Management",
    to: "/solutions/diabetes-management",
    icon: Droplet,
    match: (p) => p === "/solutions/diabetes-management",
    accent: "text-sky-600",
    ring: "hover:bg-sky-50",
    activeBg: "bg-sky-600 text-white",
  },
  {
    key: "blood-pressure-monitors",
    name: "Blood Pressure Monitors",
    to: "/marketplace?category=blood-pressure-monitors",
    icon: Stethoscope,
    match: (p, s) => s?.get?.("category") === "blood-pressure-monitors",
    accent: "text-red-700",
    ring: "hover:bg-red-50",
    activeBg: "bg-red-700 text-white",
  },
  {
    key: "di-tank",
    name: "DI Tank Exchange",
    to: "/marketplace/di-tank-exchange",
    icon: Droplets,
    match: (p) => p === "/marketplace/di-tank-exchange",
    accent: "text-sky-700",
    ring: "hover:bg-sky-50",
    activeBg: "bg-sky-700 text-white",
  },
  {
    key: "microfiber",
    name: "Microfiber Cleaning",
    to: "/marketplace?category=evs-janitorial&sub=Microfiber",
    icon: Grip,
    match: (p, s) => p.startsWith("/marketplace") && s?.get?.("category") === "evs-janitorial" && s?.get?.("sub") === "Microfiber",
    accent: "text-teal-700",
    ring: "hover:bg-teal-50",
    activeBg: "bg-teal-600 text-white",
  },
  {
    key: "green-klean",
    name: "Green Klean®",
    to: "/marketplace/green-klean",
    icon: Leaf,
    match: (p) => p.startsWith("/marketplace/green-klean") || p.startsWith("/hospital/marketplace/green-klean"),
    accent: "text-emerald-700",
    ring: "hover:bg-emerald-50",
    activeBg: "bg-emerald-700 text-white",
  },
  {
    key: "microbiology-testing",
    name: "Microbiology Testing",
    to: "/microbiology-testing",
    icon: Microscope,
    match: (p) => p === "/microbiology-testing",
    accent: "text-teal-700",
    ring: "hover:bg-teal-50",
    activeBg: "bg-teal-700 text-white",
  },
];

export default function MarketplaceCategoryNav({ currentCategory = null }) {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (cat) => {
    if (currentCategory && cat.key === currentCategory) return true;
    try {
      return cat.match(location.pathname, search);
    } catch {
      return false;
    }
  };

  return (
    <>
      {/* Desktop: top-anchored sticky left rail — Amazon-style */}
      <aside
        data-testid="marketplace-category-nav-desktop"
        className={`hidden lg:flex flex-col fixed left-0 top-24 z-30 bg-white border-r border-t border-b border-slate-200 rounded-r-lg transition-[width] duration-200 ${
          collapsed ? "w-11" : "w-52"
        }`}
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
      >
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="marketplace-category-nav-toggle"
          className="absolute -right-3 top-3 w-6 h-6 rounded-full bg-white border border-slate-300 text-slate-600 flex items-center justify-center shadow-sm hover:bg-slate-50 hover:text-slate-900 transition-colors"
          aria-label={collapsed ? "Expand marketplace nav" : "Collapse marketplace nav"}
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {!collapsed && (
          <div className="px-4 pt-3 pb-2 border-b border-slate-100">
            <p className="text-[10px] font-bold uppercase tracking-[1.5px] text-slate-500 flex items-center gap-1.5">
              <Store className="w-3 h-3" /> Shop by category
            </p>
          </div>
        )}

        <nav className="flex-1 py-1.5 overflow-y-auto max-h-[65vh]">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = isActive(cat);
            const showSubs = active && cat.subItems && !collapsed;
            return (
              <div key={cat.key}>
                <Link
                  to={cat.to}
                  data-testid={`marketplace-nav-${cat.key}`}
                  title={collapsed ? cat.name : undefined}
                  className={`flex items-center gap-2.5 text-[13px] transition-colors relative ${
                    collapsed ? "justify-center py-2" : "px-3 py-2"
                  } ${
                    active
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium"
                  }`}
                >
                  {/* Active-state indicator (left accent bar) */}
                  {active && (
                    <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-slate-900" />
                  )}
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-slate-900" : "text-slate-500"}`} />
                  {!collapsed && (
                    <span className="truncate flex items-center gap-1.5">
                      {cat.name}
                      {cat.badge && (
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-slate-900 tracking-wider">
                          {cat.badge}
                        </span>
                      )}
                    </span>
                  )}
                </Link>
                {showSubs && (
                  <div className="ml-6 border-l border-slate-200 pl-2 py-1">
                    {cat.subItems.map((sub) => (
                      <Link
                        key={sub.key}
                        to={sub.to}
                        data-testid={`marketplace-nav-${sub.key}`}
                        className="block px-2 py-1.5 text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Mobile: horizontal scrollable pill strip */}
      <div
        data-testid="marketplace-category-nav-mobile"
        className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-30"
      >
        <div className="flex items-center gap-2 px-4 py-2.5 overflow-x-auto no-scrollbar">
          <span className="text-[10px] font-bold uppercase tracking-[1.5px] text-slate-500 shrink-0 mr-1">
            Shop:
          </span>
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = isActive(cat);
            return (
              <Link
                key={cat.key}
                to={cat.to}
                data-testid={`marketplace-nav-mobile-${cat.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}
              >
                <Icon className="w-3 h-3" />
                {cat.name}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
